<template>
  <NuxtLayout name="user">
    <div class="flex h-full justify-center sm:justify-start md:gap-[140px]">
      <ProfileMenu
        class="w-full md:!w-1/4"
        :show_avatar="true"
        :avatar="avatar"
        :button-options="{
          label: $t('user.account.titles.delete_account'),
          prefix_icon: 'ic-trash',
        }"
        :accordion-items="menuItems"
        :class="{ 'hidden md:!flex': $route.path !== paths.account.index }"
        @select="handleSelect"
        @delete="isDeleteAccountModalOpen = true"
      />

      <div
        class="flex flex-1"
        :class="{ 'hidden md:!block': $route.path === paths.account.index }"
      >
        <slot />
      </div>
    </div>

    <ChangeLangModal
      :is-open="isChangeLangModalOpen"
      :languages="langOptions"
      :current-lang="currentLocale.code"
      @close="isChangeLangModalOpen = false"
      @save="handleSelectLanguage"
    />

    <LogoutModal
      :is-open="isLogoutModalOpen"
      @keep="isLogoutModalOpen = false"
      @close="isLogoutModalOpen = false"
      @logout="
        logout();
        $router.replace(paths.home);
      "
    />

    <DeleteAccountModal
      v-model:isOpen="isDeleteAccountModalOpen"
      type="feedback"
      @keep="isDeleteAccountModalOpen = false"
    />
  </NuxtLayout>
</template>

<script setup lang="ts">
import type {
  AccordionButtonOption,
  AccordionButtonProps,
} from "~/types/schemas/common/Molecule/Accordion";
import type { RadioProps } from "~/types/schemas/common/Atom/radio";
import paths from "~/constants/paths";

import ProfileMenu from "~/components/user/Organism/ProfileMenu.vue";
import LogoutModal from "~/components/user/Organism/Modals/LogoutModal.vue";
import DeleteAccountModal from "~/components/user/Organism/Modals/DeleteAccount/Modal.vue";
import ChangeLangModal from "~/components/user/Organism/Modals/ChangeLangModal.vue";
import useUser from "~/composables/user/useUser";
import useAddresses from "~/composables/user/useAddresses";

const isLogoutModalOpen = ref(false);
const isChangeLangModalOpen = ref(false);
const isDeleteAccountModalOpen = ref(false);

const { t } = useI18n();
const { availableLocales, currentLocale, setLang } = useLocale();
const router = useRouter();
const route = useRoute();

const { user, logout } = useUser();
const { fetchAddresses } = useAddresses();

const langOptions = computed<RadioProps[]>(() =>
  availableLocales.value.map((lang) => ({
    value: lang.code,
    label: lang.name,
  })),
);

const hasMultipleLanguages = computed(() => availableLocales.value.length > 1);

const avatar = computed(() => {
  if (!user.value) return;
  return `${user.value.first_name} ${user.value.last_name}`;
});

const menuItems = computed<AccordionButtonProps[]>(() => {
  const items: AccordionButtonProps[] = [
    {
      id: paths.account.orders.active,
      title: t("user.account.labels.orders"),
      titleIcon: "ic-bag",
      isSelected:
        paths.account.orders.active === route.path ||
        paths.account.orders.inactive === route.path,
      options: [
        {
          id: paths.account.orders.active,
          title: t("user.account.labels.active_orders"),
          isSelected: paths.account.orders.active === route.path,
        },
        {
          id: paths.account.orders.inactive,
          title: t("user.orders.labels.orders_history"),
          isSelected: paths.account.orders.inactive === route.path,
        },
      ],
    },
    {
      id: paths.account.details,
      title: t("user.account.labels.account_details_tab"),
      titleIcon: "ic-user",
      isSelected: route.path === paths.account.details,
    },
    {
      id: paths.account.addresses,
      title: t("user.account.labels.my_addresses_tab"),
      titleIcon: "ic-place",
      isSelected: route.path === paths.account.addresses,
    },
    {
      id: paths.account.changePassword,
      title: t("user.account.labels.change_password_tab"),
      titleIcon: "ic-lock",
      isSelected: route.path === paths.account.changePassword,
    },
    {
      id: "logout",
      title: t("user.account.labels.logout_tab"),
      titleIcon: "ic-logout",
    },
  ];

  if (hasMultipleLanguages.value) {
    items.splice(4, 0, {
      id: "lang",
      title: t("user.account.labels.change_lang_tab"),
      titleIcon: "ic-world",
    });
  }

  items.push();

  return items;
});

fetchAddresses();

const handleSelect = (option: AccordionButtonOption) => {
  if (option.id === "logout") {
    isLogoutModalOpen.value = true;
  } else if (option.id === "lang") {
    isChangeLangModalOpen.value = true;
  } else {
    router.push(option.id);
  }
};

const handleSelectLanguage = (langCode: string) => {
  if (langCode !== currentLocale.value.code) {
    const selectedLang = availableLocales.value.find(
      (item) => item.code === langCode,
    );

    if (selectedLang) setLang(selectedLang, true);
  }
};
</script>
